import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel } from "./card-handler";

const CHANNEL_OPTIONS = [
  { label: "Select Channel", value: "" },
  { label: "WhatsApp", value: "WhatsApp" },
  { label: "SMS", value: "SMS" },
  { label: "Email", value: "Email" },
];

function extractVariableSlots(content: string): number[] {
  const found = new Set<number>();
  for (const m of content.matchAll(/\{\{(\d+)\}\}/g)) {
    found.add(Number(m[1]));
  }
  return Array.from(found).sort((a, b) => a - b);
}

function renderPreview(content: string, vars: Record<number, string>): string {
  return content.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const v = vars[Number(n)];
    return v?.trim() ? v : `{{${n}}}`;
  });
}

function parseButtons(tpl: TemplateModule): string[] {
  if (!tpl.buttons || !Array.isArray(tpl.buttons)) return [];
  return tpl.buttons
    .map((b: any) => (typeof b === "string" ? b : b?.text ?? ""))
    .filter(Boolean);
}

interface SendBirthdayMessageCardProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const SendBirthdayMessageCard: React.FC<SendBirthdayMessageCardProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

  // Filter templates based on selected channel
  const whatsappTemplates = templates.filter(
    (t) => t.wa_status === "APPROVED" && (!t.channel || t.channel?.toLowerCase() === "whatsapp")
  );

  const emailTemplates = templates.filter(
    (t) => t.channel?.toLowerCase() === "email"
  );

  const smsTemplates = templates.filter(
    (t) => t.channel?.toLowerCase() === "sms"
  );

  const getFilteredTemplates = () => {
    const channel = config.channel?.toLowerCase();
    if (channel === "whatsapp") return whatsappTemplates;
    if (channel === "email") return emailTemplates;
    if (channel === "sms") return smsTemplates;
    return [];
  };

  const filteredTemplates = getFilteredTemplates();

  const selectedTemplate: TemplateModule | null = useMemo(() => {
    const configuredId = config.templateId ?? config.template_id ?? "";
    const configuredName = config.templateName ?? config.template ?? config.template_name ?? "";

    return filteredTemplates.find((t) =>
      (configuredId && t.id === configuredId) ||
      (configuredName && t.template_name === configuredName)
    ) ?? null;
  }, [config.templateId, config.template_id, config.templateName, config.template, config.template_name, filteredTemplates]);

  const variableSlots = selectedTemplate
    ? extractVariableSlots(selectedTemplate.content ?? "")
    : [];

  const variables: Record<number, string> = config.variables ?? {};
  const previewBody = selectedTemplate
    ? renderPreview(selectedTemplate.content ?? "", variables)
    : "";
  const buttons = selectedTemplate ? parseButtons(selectedTemplate) : [];

  const setVariable = (index: number, value: string) =>
    onUpdate({ ...config, variables: { ...variables, [index]: value } });

  const handleTemplateChange = (templateId: string) => {
    const tpl = filteredTemplates.find((t) => t.id === templateId) ?? null;
    const channel = config.channel || "WhatsApp";
    
    onUpdate({
      ...config,
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      language: tpl?.language ?? (channel === "Email" ? "en" : "en_US"),
      channel: config.channel,
      // WhatsApp-specific fields (only if channel is WhatsApp)
      waba_id: channel === "WhatsApp" ? (tpl?.waba_id ?? "") : undefined,
      category: channel === "WhatsApp" ? (tpl?.category ?? "") : undefined,
      wa_template_id: channel === "WhatsApp" ? (tpl?.wa_template_id ?? "") : undefined,
      // Email-specific fields
      subject: channel === "Email" ? (tpl?.subject ?? "") : undefined,
      variables: {},
    });
  };

  const handleChannelChange = (channel: string) => {
    onUpdate({
      ...config,
      channel,
      templateId: "",
      templateName: "",
      template_id: "",
      variables: {},
      // Clear WhatsApp-specific fields when switching away
      waba_id: channel === "WhatsApp" ? config.waba_id : undefined,
      wa_template_id: channel === "WhatsApp" ? config.wa_template_id : undefined,
      category: channel === "WhatsApp" ? config.category : undefined,
      // Clear email subject when switching away
      subject: channel === "Email" ? config.subject : undefined,
    });
  };

  const isWhatsApp = config.channel?.toLowerCase() === "whatsapp";
  const isEmail = config.channel?.toLowerCase() === "email";
  const isSMS = config.channel?.toLowerCase() === "sms";
  const hasTemplateSupport = isWhatsApp || isEmail || isSMS;

  // Email preview styling
  const getEmailPreviewStyle = () => ({
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    lineHeight: "1.5",
    color: "#333",
  });

  return (
    <CardShell
      title="Send Birthday Message"
      description={
        <>
          {isWhatsApp && "Sends a personalized WhatsApp birthday message to your contacts."}
          {isEmail && "Sends a personalized email birthday greeting to your contacts."}
          {isSMS && "Sends a personalized SMS birthday greeting to your contacts."}
          {!config.channel && "Select a channel to send birthday messages."}
        </>
      }
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        <div className="mb-3">
          <FieldLabel>Channel</FieldLabel>
          <div className="relative">
            <select
              value={config.channel ?? ""}
              onChange={(e) => handleChannelChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-2 top-2.5 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {hasTemplateSupport && (
          <div className="mb-3">
            <FieldLabel>Select Template</FieldLabel>
            <div className="relative">
              <select
                value={config.templateId ?? config.template_id ?? ""}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              >
                <option value="">— choose template —</option>
                {filteredTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.template_name}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-2.5 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
            </div>
            {filteredTemplates.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No {config.channel} templates found. Please create one first.
              </p>
            )}
          </div>
        )}

        {selectedTemplate && (
          <>
            {/* WhatsApp Template Preview */}
            {isWhatsApp && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-3">
                {selectedTemplate.header_type === "image" && selectedTemplate.header_content && (
                  <div className="w-full h-28 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <img
                      src={selectedTemplate.header_content}
                      alt="Template header"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const parent = e.currentTarget.parentElement;
                        if (parent) parent.style.display = "none";
                      }}
                    />
                  </div>
                )}

                <div className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                  {previewBody}
                </div>

                {selectedTemplate.footer && (
                  <div className="px-3 pb-2 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 italic border-t border-gray-100 dark:border-gray-800 pt-1.5">
                    {selectedTemplate.footer}
                  </div>
                )}

                {buttons.length > 0 && (
                  <>
                    <div className="border-t border-gray-100 dark:border-gray-800">
                      {buttons.map((btn, i) => (
                        <div key={i} className="px-3 py-1.5 text-xs text-indigo-600 text-center border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                          {btn}
                        </div>
                      ))}
                    </div>
                    <div className="px-3 py-2 space-y-1.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                      {buttons.map((btn, i) => (
                        <div key={i} className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg px-2 py-1.5 border border-gray-100 dark:border-gray-800">
                          <span className="text-xs text-gray-700 dark:text-gray-300">{btn}</span>
                          <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow" />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Email Template Preview */}
            {isEmail && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-3">
                {/* Email Subject */}
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Subject:</span>
                  <span className="text-xs text-gray-700 dark:text-gray-300 ml-2">
                    {selectedTemplate.subject || "Birthday Greeting"}
                  </span>
                </div>

                {/* Email Body Preview */}
                <div className="px-3 py-3" style={getEmailPreviewStyle()}>
                  <div className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                    {previewBody}
                  </div>
                </div>

                {/* Email Footer */}
                {selectedTemplate.footer && (
                  <div className="px-3 pb-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-2">
                    {selectedTemplate.footer}
                  </div>
                )}
              </div>
            )}

            {/* SMS Template Preview */}
            {isSMS && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-3 bg-gray-50 dark:bg-gray-800">
                <div className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed font-mono">
                  {previewBody}
                </div>
                <div className="px-3 pb-2 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 border-t border-gray-200 dark:border-gray-700 pt-1.5">
                  SMS message
                </div>
              </div>
            )}

            {/* Template Variables */}
            {variableSlots.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Template Variables</p>
                <div className="space-y-2">
                  {variableSlots.map((idx) => {
                    const varDef = selectedTemplate.variables?.find((v) => v.index === idx);
                    return (
                      <div key={idx}>
                        <label className="block text-xs mb-0.5">
                          <span className="font-medium text-gray-600 dark:text-gray-300">{`{{${idx}}}`}</span>
                          {varDef?.name && <span className="ml-1 text-[#7B7B7B] font-semibold dark:text-gray-500">— {varDef.name}</span>}
                        </label>
                        <input
                          className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-indigo-300 placeholder-gray-300 dark:placeholder-gray-500"
                          placeholder={varDef?.example ?? `Value for {{${idx}}}`}
                          value={variables[idx] ?? ""}
                          onChange={(e) => setVariable(idx, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <FieldLabel>Language</FieldLabel>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
                {selectedTemplate.language || (isWhatsApp ? "en_US" : isEmail ? "en" : "en")}
              </div>
            </div>
          </>
        )}

        {/* Show template selection hint when no template is selected but channel is chosen */}
        {/* {config.channel && !selectedTemplate && hasTemplateSupport && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700">
              {filteredTemplates.length === 0 
                ? `No ${config.channel} templates available. Please create a ${config.channel} template first.`
                : `Select a ${config.channel} template above to configure the birthday message.`}
            </p>
          </div>
        )} */}
    </CardShell>
  );
};